// SPEC manna-web-ui §6.5 — single source of truth for money formatting +
// parsing. Every UI surface that shows or captures a monetary amount goes
// through this module so we get consistent symbols, separators, and
// validation across deals, quotes, commissions, etc.

export interface CurrencyOption {
  code: string;
  symbol: string;
  label: string;
}

// v1 list. SPEC §6.5: extend via ref_currencies, not by hard-coding new
// entries here. Order = display order in the picker.
export const SUPPORTED_CURRENCIES: readonly CurrencyOption[] = [
  { code: "USD", symbol: "$", label: "US Dollar" },
  { code: "EUR", symbol: "€", label: "Euro" },
  { code: "GBP", symbol: "£", label: "British Pound" },
  { code: "CAD", symbol: "CA$", label: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", label: "Australian Dollar" },
];

export function currencySymbol(code: string): string {
  return SUPPORTED_CURRENCIES.find((c) => c.code === code)?.symbol ?? code;
}

// Strip everything that isn't a digit, dot, or comma. Then heuristically
// decide which separator is the decimal point and which is grouping —
// supporting both `$1,234.56` (US) and `1.234,56` (EU) paste shapes.
//
// Rules:
//   1. If both `.` and `,` appear, the LAST one is the decimal point and
//      the other is grouping (drop it).
//   2. If only one separator type appears multiple times → all instances
//      are grouping (e.g. `1,234,567` → 1234567, `1.234.567` → 1234567).
//   3. If only one separator and it appears once:
//        • exactly 3 digits after → grouping (US `1,234` = 1234)
//        • otherwise → decimal (`1,5` = 1.5; `1.5` = 1.5)
// Empty string → null. Anything that doesn't parse to a finite number → null.
export function parseAmount(input: string | null | undefined): string | null {
  if (input == null) return null;
  const cleaned = input.replace(/[^\d.,-]/g, "").trim();
  if (!cleaned) return null;

  const dots = (cleaned.match(/\./g) ?? []).length;
  const commas = (cleaned.match(/,/g) ?? []).length;

  let decimalChar: "." | "," | null = null;
  if (dots > 0 && commas > 0) {
    decimalChar = cleaned.lastIndexOf(".") > cleaned.lastIndexOf(",") ? "." : ",";
  } else if (dots > 1 || commas > 1) {
    decimalChar = null; // all grouping
  } else if (dots === 1 || commas === 1) {
    const sep = dots === 1 ? "." : ",";
    const idx = cleaned.lastIndexOf(sep);
    const trailing = cleaned.length - idx - 1;
    decimalChar = trailing === 3 ? null : (sep as "." | ",");
  }

  let normalized: string;
  if (decimalChar === null) {
    normalized = cleaned.replace(/[.,]/g, "");
  } else {
    const idx = cleaned.lastIndexOf(decimalChar);
    const intPart = cleaned.slice(0, idx).replace(/[.,]/g, "");
    const fracPart = cleaned.slice(idx + 1).replace(/[.,]/g, "");
    normalized = fracPart ? `${intPart}.${fracPart}` : intPart;
  }

  const n = Number(normalized);
  if (!Number.isFinite(n)) return null;
  return normalized;
}

// Format an in-progress amount string for DISPLAY in an input: group the
// integer part with thousands commas (US style) while preserving a trailing
// decimal portion as typed. Used by MoneyField so the user sees `1,000` as they
// type. parseAmount() reverses the grouping when reporting the value upstream.
export function formatAmountInput(input: string | null | undefined): string {
  if (input == null) return "";
  let s = input.replace(/[^\d.,-]/g, "");
  const negative = s.startsWith("-");
  s = s.replace(/-/g, "").replace(/,/g, ""); // drop sign + existing grouping
  const firstDot = s.indexOf(".");
  let intPart: string;
  let decPart: string | null;
  if (firstDot >= 0) {
    intPart = s.slice(0, firstDot).replace(/\./g, "");
    decPart = s.slice(firstDot + 1).replace(/\./g, "");
  } else {
    intPart = s;
    decPart = null;
  }
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  let out = decPart !== null ? `${grouped}.${decPart}` : grouped;
  if (negative && out !== "") out = `-${out}`;
  return out;
}

// Validates an already-parsed amount string. Used by callers that need to
// gate submit; MoneyField also calls this to drive helperText.
export function isValidAmount(parsed: string | null): boolean {
  if (parsed === null) return true; // empty = valid (null amount)
  const n = Number(parsed);
  if (!Number.isFinite(n) || n < 0) return false;
  // ≤ 2 fraction digits
  const dot = parsed.indexOf(".");
  if (dot >= 0 && parsed.length - dot - 1 > 2) return false;
  return true;
}

// Display format for lists, detail pages, kanban cards. Null amount → em
// dash. Unknown currency code → numeric only.
export function formatMoney(args: {
  amount: number | string | null | undefined;
  currency?: string | null;
}): string {
  const { amount, currency } = args;
  if (amount === null || amount === undefined || amount === "") return "—";
  const n = typeof amount === "number" ? amount : Number(amount);
  if (!Number.isFinite(n)) return "—";

  const code = currency ?? "USD";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);
  }
}
